import { Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AlertComponent,
  IconComponent,
  InputComponent,
  TooltipComponent,
  TypographyComponent,
} from '@purolator/ui-kit-angular';
import { TranslatePipe } from '../../../../../core/i18n';
import {
  FloatingSelect,
  type FloatingSelectOption,
} from '../../../../../shared/components/floating-select/floating-select';
import { containsDisallowedEmoji } from '../../../../../shared/validators/emoji.validators';
import { MAX_COURIER_INSTRUCTIONS_LENGTH } from '../pickup-drop-off.state';

/** Read-only Ship From lines shown under Pickup Address & Contact Info. */
export interface PickupAddressDisplay {
  streetLine: string | null;
  name: string | null;
  phone: string | null;
  buzzer: string | null;
}

/**
 * Figma “Pickup Address & Contact Info” block (node 19018:57804):
 * read-only Ship From summary, pickup-from location select, and courier instructions.
 */
@Component({
  selector: 'app-pickup-address-contact-section',
  imports: [
    TypographyComponent,
    TranslatePipe,
    FloatingSelect,
    InputComponent,
    TooltipComponent,
    IconComponent,
    FormsModule,
    AlertComponent,
  ],
  templateUrl: './pickup-address-contact-section.component.html',
  styleUrl: './pickup-address-contact-section.component.scss',
})
export class PickupAddressContactSectionComponent {
  readonly address = input.required<PickupAddressDisplay | null>();
  readonly locationOptions = input.required<FloatingSelectOption[]>();
  readonly locationCode = input<string | null>(null);
  readonly courierInstructions = input('');
  readonly locationsLoading = input(false);
  readonly locationsError = input<string | null>(null);
  /**
   * When false, hide Pickup Location and Courier Instructions (pickup cannot proceed).
   * Defaults to true so hosts that omit the input keep the scheduling fields.
   */
  readonly showSchedulingFields = input(true);

  /** Emits the selected pickup-from location code. */
  readonly locationCodeChange = output<string | null>();
  /** Emits courier instructions truncated to {@link MAX_COURIER_INSTRUCTIONS_LENGTH}. */
  readonly courierInstructionsChange = output<string>();

  protected readonly maxInstructionsLength = MAX_COURIER_INSTRUCTIONS_LENGTH;

  /**
   * True when courier instructions contain emoji (OM-718); reuses restricted-characters copy.
   */
  protected readonly hasCourierInstructionsEmojiError = computed(() =>
    containsDisallowedEmoji(this.courierInstructions()),
  );

  /**
   * Emits courier instructions as entered. Length is enforced by the kit input
   * `maxLength` (native maxlength) so the customer cannot type past 120 characters
   * and values are not silently truncated in this handler.
   */
  onInstructionsChange(value: string | null | undefined): void {
    this.courierInstructionsChange.emit(value ?? '');
  }

  /**
   * Emits the selected pickup location code from the floating select.
   */
  onLocationChange(value: string | null): void {
    this.locationCodeChange.emit(value);
  }

  /** Whether the address summary has any non-empty lines to render. */
  protected readonly hasAddress = computed(() => {
    const address = this.address();
    return Boolean(
      address && (address.streetLine || address.name || address.phone || address.buzzer),
    );
  });
}
